import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Mermaid from './Mermaid';
import QuizCard from './QuizCard';
import {
  IconChevronDown,
  IconDecision,
  IconEdit,
  IconMore,
  IconPeople,
  IconPin,
  IconReact,
  IconReply,
  IconSparkle,
  IconTodo,
  IconTrash,
} from './Icons';
import AIBadge from './AIBadge';
import { BrandMark } from './BrandMark';
import { isImageIcon } from './EntityIcon';
import { avatarImageSrc } from '../utils/avatar';
import { renderMentions } from '../mentions';
import './MessageBubble.css';

function linkify(text, participants) {
  // Match URLs but exclude trailing punctuation (.,;:!?) that's not part of the URL
  const urlRegex = /(https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]'"])/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </a>
    ) : (
      // Colour @mentions of any participant (human or AI) in their own
      // colour — same rendering as the composer's highlight layer.
      <React.Fragment key={i}>{renderMentions(part, participants, i)}</React.Fragment>
    ),
  );
}

const EMBED_PATTERNS = [
  {
    name: 'youtube',
    test: /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/,
    render: (url, match) => (
      <div className="link-embed youtube-embed">
        <iframe
          src={`https://www.youtube.com/embed/${match[1]}`}
          title="YouTube video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    ),
  },
  {
    name: 'google-maps',
    test: /(?:google\.com\/maps|maps\.google\.com|goo\.gl\/maps|maps\.app\.goo\.gl)/,
    render: (url) => (
      <a className="link-embed maps-embed" href={url} target="_blank" rel="noopener noreferrer">
        <span className="embed-icon">📍</span>
        <span className="embed-info">
          <span className="embed-label">Google Maps</span>
          <span className="embed-url">{url.length > 60 ? url.slice(0, 60) + '...' : url}</span>
        </span>
        <span className="embed-action">Open Map →</span>
      </a>
    ),
  },
  {
    name: 'pinterest',
    test: /(?:pinterest\.com\/pin\/|pin\.it\/)/,
    render: (url) => (
      <a
        className="link-embed pinterest-embed"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="embed-icon">📌</span>
        <span className="embed-info">
          <span className="embed-label">Pinterest Pin</span>
          <span className="embed-url">{url.length > 60 ? url.slice(0, 60) + '...' : url}</span>
        </span>
        <span className="embed-action">View Pin →</span>
      </a>
    ),
  },
];

function extractEmbeds(text) {
  const urlRegex = /(https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]'"])/g;
  const urls = text.match(urlRegex) || [];
  const embeds = [];
  const seen = new Set();
  for (const url of urls) {
    if (seen.has(url)) continue;
    seen.add(url);
    for (const pattern of EMBED_PATTERNS) {
      const match = url.match(pattern.test);
      if (match) {
        embeds.push({ key: url, element: pattern.render(url, match) });
        break;
      }
    }
  }
  return embeds;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType) {
  if (mimeType?.startsWith('image/')) return '\uD83D\uDDBC';
  if (mimeType === 'application/pdf') return '\uD83D\uDCC4';
  if (mimeType?.includes('zip') || mimeType?.includes('archive')) return '\uD83D\uDCE6';
  return '\uD83D\uDCCE';
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '🤔', '👀'];

// Stable per-user fallback color when the server didn't ship one (e.g. for an
// older replied-to message). Hashing the id keeps each user on the same hue
// across renders even without a participant lookup.
const FALLBACK_COLORS = [
  '#3B82F6',
  '#EF4444',
  '#10B981',
  '#F59E0B',
  '#EC4899',
  '#6366F1',
  '#14B8A6',
  '#F97316',
  '#06B6D4',
  '#84CC16',
];
export function colorForId(id) {
  if (!id) return FALLBACK_COLORS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
}

// Per-human-user message-bubble background. Each human user is assigned a
// stable pastel (hashed off their id) so you can tell speakers apart at a
// glance by the colour of their bubbles, including your own. The palette is
// deliberately soft (light pastels on warm paper, so the dark ink text stays
// readable) and deliberately EXCLUDES the two AI brand hues — terracotta
// (#d96f43) and blue-green/teal (#127c6d) — so a human bubble is never
// mistaken for an AI's. Distinct from `colorForId`, which is the saturated
// identity colour used for the sender name/avatar/mentions.
const PASTEL_BUBBLE_BACKGROUNDS = [
  '#EDE7FA', // lavender
  '#DEEBF8', // powder blue
  '#FADFEC', // blush pink
  '#F6F0CE', // butter yellow
  '#E6EFD9', // sage
  '#E1E4F7', // periwinkle
  '#F4DEF1', // orchid
  '#DCEDF6', // sky
];
export function pastelBackgroundForId(id) {
  if (!id) return PASTEL_BUBBLE_BACKGROUNDS[0];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return PASTEL_BUBBLE_BACKGROUNDS[Math.abs(hash) % PASTEL_BUBBLE_BACKGROUNDS.length];
}

// Aimy + Aiden use the platform's brand colors (terracotta + blue-green).
// We also resolve by senderId/senderName here so historical messages keep
// their AI's identity even when that AI is no longer in the room's live
// participant list (e.g. when GEMINI_API_KEY is unset Aiden vanishes from
// aiParticipants, but his past replies must still look like Aiden, not Aimy).
const DEFAULT_AI_COLOR = '#d96f43';
const AI_PERSONA_COLORS_BY_ID = {
  'ai-assistant': '#d96f43',
  'ai-aiden': '#127c6d',
};
const AI_PERSONA_COLORS_BY_NAME = {
  aimy: '#d96f43',
  aiden: '#127c6d',
};
function aiColorForSender(senderId, senderName) {
  if (senderId && AI_PERSONA_COLORS_BY_ID[senderId]) return AI_PERSONA_COLORS_BY_ID[senderId];
  if (senderName) {
    const key = String(senderName).trim().toLowerCase();
    if (AI_PERSONA_COLORS_BY_NAME[key]) return AI_PERSONA_COLORS_BY_NAME[key];
  }
  return DEFAULT_AI_COLOR;
}

// The avatar background is a faded version of the *opposite* brand colour
// from the sparkle, so Aimy reads as terracotta-on-blue-green and Aiden as
// blue-green-on-terracotta while leaving the solid sparkle clearly in front.
function aiBackgroundForSender(senderId, senderName) {
  const sparkle = aiColorForSender(senderId, senderName);
  return sparkle === '#127c6d' ? 'rgba(217, 111, 67, 0.22)' : 'rgba(18, 124, 109, 0.22)';
}

function initialsFor(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Renders Aimy's avatar (3 little sparkles) or any other user's avatar
// (emoji if set, otherwise initials on a coloured disc).
function SenderAvatar({ senderType, senderId, color, avatar, name, size = 26 }) {
  if (senderType === 'ai') {
    const bg = aiBackgroundForSender(senderId, name);
    return (
      <span
        className="sender-avatar ai-avatar"
        aria-label={`${name || 'AI'} avatar`}
        style={{
          width: size,
          height: size,
          color: color || DEFAULT_AI_COLOR,
          background: bg,
          borderColor: bg,
        }}
      >
        <IconSparkle size={Math.round(size * 0.72)} />
      </span>
    );
  }
  if (avatar) {
    return (
      <span
        className={`sender-avatar emoji-avatar ${isImageIcon(avatar) ? 'image-avatar' : ''}`}
        style={{
          width: size,
          height: size,
          borderColor: color,
          background: color ? `${color}22` : undefined,
        }}
        aria-hidden="true"
      >
        {isImageIcon(avatar) ? (
          <img className="sender-avatar-img" src={avatarImageSrc(avatar)} alt="" />
        ) : (
          avatar
        )}
      </span>
    );
  }
  return (
    <span
      className="sender-avatar initial-avatar"
      style={{ width: size, height: size, background: color }}
      aria-hidden="true"
    >
      {initialsFor(name)}
    </span>
  );
}

// Tiny pill rendered next to the sender's name on human messages,
// echoing the composer chip they picked when sending. Drops to nothing
// for older messages (no `intent`) or any unrecognised value.
const INTENTS = {
  collaborate: { label: 'Symphonate', Icon: BrandMark, className: 'collaborate' },
  team: { label: 'Team', Icon: IconPeople, className: 'team' },
  ai: { label: 'Ask AI', Icon: IconSparkle, className: 'ai' },
};
function IntentBadge({ intent }) {
  const meta = INTENTS[intent];
  if (!meta) return null;
  const { label, Icon, className } = meta;
  return (
    <span className={`intent-badge intent-badge-${className}`} title={`Sent via ${label}`}>
      <Icon size={11} />
    </span>
  );
}

// Grounding footer — shows the sources an AI reply rests on. Web sources link
// out; workspace objects (artifacts/decisions/short lists/threads) render as
// labelled chips so readers can see what the answer was grounded in.
const CITATION_KIND_META = {
  web: { icon: '🔗', label: 'web' },
  artifact: { icon: '📄', label: 'artifact' },
  decision: { icon: '⚖️', label: 'decision' },
  'short-list': { icon: '📋', label: 'short list' },
  thread: { icon: '#', label: 'thread' },
};

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function CitationsFooter({ citations }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!Array.isArray(citations) || citations.length === 0) return null;
  return (
    <div className="message-citations" aria-label="Sources">
      <button
        type="button"
        className="message-citations-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <IconChevronDown
          size={12}
          className={`message-citations-chevron${expanded ? ' is-open' : ''}`}
        />
        <span className="message-citations-label">Sources</span>
        <span className="message-citations-count">{citations.length}</span>
      </button>
      {expanded &&
        citations.map((c, i) => {
          const meta = CITATION_KIND_META[c.kind] || { icon: '•', label: c.kind };
          if (c.kind === 'web' && c.url) {
            return (
              <a
                key={`${c.url}-${i}`}
                className="citation-chip citation-web"
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                title={c.url}
              >
                <span className="citation-icon">{meta.icon}</span>
                <span className="citation-title">{c.title || hostnameOf(c.url)}</span>
              </a>
            );
          }
          return (
            <span
              key={`${c.kind}-${c.id || i}`}
              className={`citation-chip citation-${c.kind}`}
              title={`${meta.label}: ${c.title}`}
            >
              <span className="citation-icon">{meta.icon}</span>
              <span className="citation-title">{c.title}</span>
            </span>
          );
        })}
    </div>
  );
}

// react-markdown renders each node by using these overrides as the *component
// type* for that node. If this object were recreated on every render (e.g.
// defined inline in the JSX), every override would have a new function identity,
// so React would unmount and remount the whole rendered subtree on each parent
// re-render — wiping the local state of any interactive child (the QuizCard's
// selected answers, etc.). The chat re-renders constantly (typing/presence/new
// messages), so the quiz would "forget" earlier answers the moment you touched
// another question. Defining the map once at module scope keeps the identities
// stable so the subtree is preserved across re-renders. Safe to hoist because
// none of these overrides close over component props or state.
const MARKDOWN_REMARK_PLUGINS = [remarkGfm];

// Exported for unit tests: the inline ```mermaid / ```quiz fence detection in
// the `code` override is a recurring source of diagram breakage (a
// case-sensitive match once made uppercase ```Mermaid fall back to raw text),
// so it's worth testing the mapping directly.
export const MARKDOWN_COMPONENTS = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  // Constrain embedded/markdown images so an uploaded picture shows
  // inline without blowing out the bubble width.
  img: ({ src, alt }) => (
    <img
      className="message-md-image"
      src={src}
      alt={alt || ''}
      loading="lazy"
      onClick={() => src && window.open(src, '_blank')}
    />
  ),
  // GFM tables can be wide — let them scroll horizontally instead
  // of overflowing the bubble.
  table: ({ children }) => (
    <div className="message-table-wrap">
      <table>{children}</table>
    </div>
  ),
  // Render fenced ```mermaid blocks as live diagrams and ```quiz
  // blocks as an interactive quiz; everything else falls through to
  // a normal <code> element.
  code: ({ inline, className, children, ...props }) => {
    const isMermaid = /language-mermaid/i.test(className || '');
    const isQuiz = /language-quiz/i.test(className || '');
    if (!inline && isMermaid) {
      return (
        <div className="message-diagram">
          <Mermaid content={String(children).replace(/\n$/, '')} />
        </div>
      );
    }
    if (!inline && isQuiz) {
      return <QuizCard content={String(children).replace(/\n$/, '')} />;
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

function MessageBubble({
  message,
  isOwn,
  participants,
  isStreaming,
  currentUserId,
  onToggleReaction,
  onReply,
  onEdit,
  onDelete,
  onPin,
  onAskAI,
  onCaptureDecision,
  onCaptureTodo,
  onOpenReplies,
  replyCount = 0,
  repliedMessage,
  onJumpToMessage,
  onCompact,
}) {
  const [showPicker, setShowPicker] = React.useState(false);
  // On desktop, hover shows only the ... button (showMoreButton). Clicking it
  // shows the full action toolbar (showActions). On touch, the ... button is
  // always visible and tapping it or the bubble toggles showActions.
  const [showMoreButton, setShowMoreButton] = React.useState(false);
  const [showActions, setShowActions] = React.useState(false);
  // Vertical offset (px from the bubble top) at which to float the toolbar. It
  // tracks where the pointer is, so on a tall message the menu appears right
  // where you're hovering/tapping instead of pinned to the top — no scrolling
  // up to reach it. `null` falls back to the CSS default (top of the bubble).
  const [actionsTop, setActionsTop] = React.useState(null);
  const [compactOfferDismissed, setCompactOfferDismissed] = React.useState(false);
  const bubbleRef = React.useRef(null);
  const hideTimerRef = React.useRef(null);
  const { senderName, senderType, content, timestamp, reactions, edited, pinned } = message;

  React.useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // Small hide delay lets the mouse traverse the gap between the bubble
  // body and the floating action toolbar without flickering closed — the
  // root cause of the "reply button isn't clickable" UX bug.
  const scheduleHideActions = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setShowMoreButton(false);
      setShowActions(false);
      setShowPicker(false);
    }, 220);
  };

  const cancelHideActions = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  // Float the action toolbar next to where the pointer is (vertically), so on a
  // tall message it appears at the hover/tap point instead of pinned to the top
  // of the bubble. Clamp within the bubble so it never spills past the edges.
  const ACTIONS_HEIGHT = 34;
  const positionActionsAt = (clientY) => {
    const el = bubbleRef.current;
    if (!el || typeof clientY !== 'number') return;
    const rect = el.getBoundingClientRect();
    const margin = 4;
    const centered = clientY - rect.top - ACTIONS_HEIGHT / 2;
    const max = Math.max(margin, rect.height - ACTIONS_HEIGHT - margin);
    setActionsTop(Math.min(Math.max(centered, margin), max));
  };

  // When the actions toolbar is visible, keep it tracking the cursor as it reads
  // down a long message, but freeze once the pointer is over the toolbar/picker
  // itself so it doesn't run away as the user moves in to click a button.
  const trackPointer = (e) => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) return;
    if (e.target.closest('.message-actions, .reaction-picker, .message-more-btn')) return;
    if (!showActions) return; // Only track if toolbar is actually open
    positionActionsAt(e.clientY);
  };

  // Touch affordance: desktop reveals the action toolbar (reply/react +
  // capture) on hover, but a touch device has no hover — so on a coarse/no-hover
  // pointer, tapping the message body reveals that toolbar. This is what the
  // composer hint ("Tap a message to capture it") promises. Taps on interactive
  // children (links, buttons, attachment images, embeds, the reply reference)
  // are ignored so they keep their own behaviour.
  const revealActionsOnTap = (e) => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (!window.matchMedia('(hover: none)').matches) return; // desktop uses hover
    if (e.target.closest('a, button, img, iframe, input, textarea, .reply-reference')) return;
    cancelHideActions();
    positionActionsAt(e.clientY);
    setShowActions(true);
  };

  React.useEffect(() => {
    if (!showActions) return;
    const handleOutside = (e) => {
      if (bubbleRef.current && !bubbleRef.current.contains(e.target)) {
        setShowActions(false);
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showActions]);

  if (senderType === 'system') {
    // Interactive "compact this thread?" offer: the server tags it with
    // kind 'compact-offer'. Render Compact / Not now actions. "Not now" just
    // hides it locally — the server's per-thread throttle stops it re-asking.
    if (message.kind === 'compact-offer') {
      if (compactOfferDismissed) return null;
      return (
        <div className="message-system compact-offer">
          <span>{content}</span>
          <div className="compact-offer-actions">
            <button
              type="button"
              className="compact-offer-btn primary"
              onClick={() => {
                setCompactOfferDismissed(true);
                if (onCompact) onCompact();
              }}
            >
              Compact
            </button>
            <button
              type="button"
              className="compact-offer-btn"
              onClick={() => setCompactOfferDismissed(true)}
            >
              Not now
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="message-system">
        <span>{content}</span>
      </div>
    );
  }

  const isAI = senderType === 'ai';
  const participant = participants.find((p) => p.id === message.senderId);
  const color =
    participant?.color ||
    (isAI
      ? aiColorForSender(message.senderId, senderName)
      : colorForId(message.senderId || senderName));
  const avatar = participant?.avatar;

  // Each human user gets a stable pastel bubble background so speakers are
  // distinguishable at a glance (own messages included). AI/system bubbles
  // keep their own CSS backgrounds.
  const humanBubbleBackground =
    senderType === 'human' ? pastelBackgroundForId(message.senderId || senderName) : undefined;

  // Reply-reference colour/avatar — looked up against the same participants
  // list so the replied-to user's identity is visually consistent.
  const repliedParticipant = repliedMessage
    ? participants.find((p) => p.id === repliedMessage.senderId)
    : null;
  const repliedColor = repliedMessage
    ? repliedParticipant?.color ||
      (repliedMessage.senderType === 'ai'
        ? aiColorForSender(repliedMessage.senderId, repliedMessage.senderName)
        : colorForId(repliedMessage.senderId || repliedMessage.senderName))
    : null;
  const repliedAvatar = repliedParticipant?.avatar;

  const time = new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      ref={bubbleRef}
      id={message.id ? `msg-${message.id}` : undefined}
      className={`message-bubble ${isOwn ? 'own' : ''} ${isAI ? 'ai' : ''} ${pinned ? 'pinned' : ''} ${showMoreButton ? 'more-button-visible' : ''} ${showActions ? 'actions-visible' : ''}`}
      style={humanBubbleBackground ? { background: humanBubbleBackground } : undefined}
      onMouseEnter={() => {
        if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) {
          return; // On touch devices, skip desktop hover behavior
        }
        cancelHideActions();
        setShowMoreButton(true);
      }}
      onMouseMove={trackPointer}
      onMouseLeave={scheduleHideActions}
      onClick={revealActionsOnTap}
    >
      {/* Reply reference — shows the original sender's avatar + colour so the
          replied-to user's identity is unmistakable. */}
      {repliedMessage &&
        (() => {
          const snippet =
            repliedMessage.content.length > 60
              ? repliedMessage.content.slice(0, 60) + '...'
              : repliedMessage.content;
          const canJump = Boolean(onJumpToMessage && repliedMessage.id);
          const handleJump = () => {
            if (canJump) onJumpToMessage(repliedMessage.id);
          };
          const refStyle = { borderLeftColor: repliedColor };
          const inner = (
            <>
              <SenderAvatar
                senderType={repliedMessage.senderType}
                senderId={repliedMessage.senderId}
                color={repliedColor}
                avatar={repliedAvatar}
                name={repliedMessage.senderName}
                size={20}
              />
              <span className="reply-reference-body">
                <span className="reply-reference-name" style={{ color: repliedColor }}>
                  {repliedMessage.senderName}
                  {repliedMessage.senderType === 'ai' && (
                    <>
                      {' '}
                      <AIBadge />
                    </>
                  )}
                </span>
                <span className="reply-reference-text">{snippet}</span>
              </span>
            </>
          );
          return canJump ? (
            <button
              type="button"
              className="reply-reference reply-reference-clickable"
              onClick={handleJump}
              title="Jump to original message"
              style={refStyle}
            >
              {inner}
            </button>
          ) : (
            <div className="reply-reference" style={refStyle}>
              {inner}
            </div>
          );
        })()}

      <div className="message-header">
        {pinned && (
          <span className="pin-indicator" aria-label="Pinned message" title="Pinned">
            📌
          </span>
        )}
        <SenderAvatar
          senderType={senderType}
          senderId={message.senderId}
          color={color}
          avatar={avatar}
          name={senderName}
          size={26}
        />
        <span className="sender-name" style={isOwn ? undefined : { color }}>
          {senderName}
        </span>
        {isAI && <AIBadge />}
        {senderType === 'human' && message.intent && <IntentBadge intent={message.intent} />}
        {edited && <span className="edited-badge">(edited)</span>}
        <span className="message-time">{time}</span>
      </div>
      <div className="message-content">
        {isAI ? (
          <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS} components={MARKDOWN_COMPONENTS}>
            {content}
          </ReactMarkdown>
        ) : (
          linkify(content, participants)
        )}
        {isStreaming && <span className="cursor-blink">|</span>}
      </div>

      {/* Grounding sources — only on finalised AI messages */}
      {isAI && !isStreaming && <CitationsFooter citations={message.citations} />}

      {/* Rich link embeds */}
      {(() => {
        const embeds = extractEmbeds(content);
        return embeds.length > 0 ? (
          <div className="embed-list">
            {embeds.map((e) => (
              <React.Fragment key={e.key}>{e.element}</React.Fragment>
            ))}
          </div>
        ) : null;
      })()}

      {/* Attachments */}
      {message.attachments && message.attachments.length > 0 && (
        <div className="attachment-list">
          {message.attachments.map((att) =>
            att.isImage ? (
              <div key={att.id} className="attachment-image">
                <img
                  src={att.url}
                  alt={att.originalName}
                  loading="lazy"
                  onClick={() => window.open(att.url, '_blank')}
                />
              </div>
            ) : (
              <a
                key={att.id}
                className="attachment-file"
                href={att.url}
                download={att.originalName}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="attachment-file-icon">{getFileIcon(att.mimeType)}</span>
                <span className="attachment-file-name">{att.originalName}</span>
                <span className="attachment-file-size">{formatFileSize(att.sizeBytes)}</span>
              </a>
            ),
          )}
        </div>
      )}

      {/* Reply count badge — opens the Replies panel */}
      {replyCount > 0 && onOpenReplies && (
        <button
          className="replies-count-badge"
          onClick={() => onOpenReplies(message)}
          title="View replies"
        >
          {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
        </button>
      )}

      {/* Reactions display */}
      {reactions && Object.keys(reactions).length > 0 && (
        <div className="reactions-bar">
          {Object.entries(reactions).map(([emoji, users]) => {
            const hasReacted = users.some((u) => u.userId === currentUserId);
            return (
              <button
                key={emoji}
                className={`reaction-chip ${hasReacted ? 'reacted' : ''}`}
                onClick={() => onToggleReaction?.(message.id, emoji)}
                title={users.map((u) => u.userName).join(', ')}
              >
                {emoji} {users.length}
              </button>
            );
          })}
        </div>
      )}

      {/* "more" button — visible on touch devices always, on desktop when hovering */}
      {!isStreaming && (
        <button
          className="message-more-btn"
          onClick={(e) => {
            e.stopPropagation();
            cancelHideActions();
            positionActionsAt(e.clientY);
            setShowActions(true);
          }}
          aria-label="Message actions"
        >
          <IconMore size={14} />
        </button>
      )}

      {/* Action toolbar (hover on desktop, tap on touch) */}
      {!isStreaming && showActions && (
        <div
          className="message-actions"
          style={actionsTop !== null ? { top: actionsTop } : undefined}
          onMouseEnter={cancelHideActions}
          onMouseLeave={scheduleHideActions}
        >
          {onToggleReaction && (
            <button
              className="action-btn"
              onClick={() => setShowPicker(!showPicker)}
              title="React"
              aria-label="Add reaction"
            >
              <IconReact size={14} />
            </button>
          )}
          {onReply && (
            <button
              className="action-btn"
              onClick={() => onReply(message)}
              title="Reply"
              aria-label="Reply to this message"
            >
              <IconReply size={14} />
            </button>
          )}
          {/* One-tap capture — promote this message into a decision or todo.
              On both human and AI messages (AI replies are prime capture
              material). Themed brand hues: terracotta decision, teal todo. */}
          {onCaptureDecision && (
            <button
              className="action-btn action-btn-decision"
              onClick={() => {
                onCaptureDecision(message);
                setShowActions(false);
              }}
              title="Save as decision"
              aria-label="Save as decision"
            >
              <IconDecision size={14} />
            </button>
          )}
          {onCaptureTodo && (
            <button
              className="action-btn action-btn-todo"
              onClick={() => {
                onCaptureTodo(message);
                setShowActions(false);
              }}
              title="Save as todo"
              aria-label="Save as todo"
            >
              <IconTodo size={14} />
            </button>
          )}
          {onAskAI && senderType === 'human' && (
            <button
              className="action-btn"
              onClick={() => onAskAI(message)}
              title="Ask the AIs to read and respond to this message"
              aria-label="Ask AI"
            >
              <IconSparkle size={14} />
            </button>
          )}
          {isOwn && onEdit && senderType === 'human' && (
            <button
              className="action-btn"
              onClick={() => onEdit(message)}
              title="Edit"
              aria-label="Edit message"
            >
              <IconEdit size={14} />
            </button>
          )}
          {onPin && senderType !== 'system' && (
            <button
              className={`action-btn ${pinned ? 'action-btn-pinned' : ''}`}
              onClick={() => onPin(message)}
              title={pinned ? 'Unpin' : 'Pin'}
              aria-label={pinned ? 'Unpin message' : 'Pin message'}
            >
              <IconPin size={14} />
            </button>
          )}
          {isOwn && onDelete && senderType === 'human' && (
            <button
              className="action-btn action-btn-danger"
              onClick={() => onDelete(message)}
              title="Delete"
              aria-label="Delete message"
            >
              <IconTrash size={14} />
            </button>
          )}
        </div>
      )}

      {/* Reaction picker */}
      {showPicker && (
        <div
          className="reaction-picker"
          style={actionsTop !== null ? { top: actionsTop } : undefined}
        >
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              className="reaction-option"
              onClick={() => {
                onToggleReaction(message.id, emoji);
                setShowPicker(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
